import type { ProductStatus, PublishBlocker } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import {
  BLOCKER_LABELS,
  useInvalidateProducts,
  useInvalidateRecommendedProducts,
  useProductsListQuery,
} from '@/entities/product';
import { useDeleteDraft, useHideProduct, usePublishProduct } from '@/features/catalog';
import { resetSettledRequests } from '@/shared/lib';
import { EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { ProductRow } from './product-row';

interface ProductsTableProps {
  readonly page: number;
  readonly status: ProductStatus | '';
  readonly search: string;
  readonly onPageChange: (page: number) => void;
}

/** 상품 목록 표 — 조회·발행·숨김·삭제·페이지 이동을 묶은 자립 블록. 추천 순위는 랜딩 관리에서 정한다. */
export const ProductsTable = ({
  page,
  status,
  search,
  onPageChange,
}: ProductsTableProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  // 숨김은 랜딩 화면의 추천 목록도 바꾼다(숨긴 상품은 추천에서 빠진다) — 목록 표와 함께 무효화한다.
  const invalidateRecommended = useInvalidateRecommendedProducts();
  const productsQuery = useProductsListQuery({ page, status, search });

  const publishAction = usePublishProduct({ onSuccess: invalidate.list });
  const hideAction = useHideProduct({ onSuccess: invalidateRecommended });
  const deleteAction = useDeleteDraft({ onSuccess: invalidate.list });

  const [publishBlockers, setPublishBlockers] = useState<{
    productName: string;
    conditions: readonly PublishBlocker[];
  } | null>(null);
  const mutationError = publishAction.error ?? hideAction.error ?? deleteAction.error;

  // 발행·숨김·삭제는 오류 한 줄(mutationError)을 함께 쓴다. 새 요청을 보내기 전에 세 요청의
  // 지난 오류를 모두 지운다 — 아니면 다른 요청을 해도 옛 오류가 그 요청이 다시 돌 때까지 남는다.
  // 표에는 요청 사이의 잠금이 없어 다른 요청이 진행 중일 수 있다 — 진행 중인 요청은 지우지 않는다.
  const clearMutationErrors = (): void => {
    resetSettledRequests([publishAction, hideAction, deleteAction]);
  };

  const handlePublish = async (productId: string, productName: string): Promise<void> => {
    setPublishBlockers(null);
    clearMutationErrors();

    const result = await publishAction.publish(productId);

    if (result && !result.published) {
      setPublishBlockers({ productName, conditions: result.unmetConditions });
    }
  };

  const handleHide = (productId: string): void => {
    clearMutationErrors();
    hideAction.hide(productId);
  };

  const handleDeleteDraft = (productId: string, productName: string): void => {
    if (window.confirm(`등록 중인 상품 "${productName}" 을(를) 삭제하시겠습니까?`)) {
      clearMutationErrors();
      deleteAction.deleteDraft(productId);
    }
  };

  return (
    <>
      {mutationError ? (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {publishBlockers ? (
        <p
          role="alert"
          className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <span className="font-medium">{publishBlockers.productName}</span> 발행 불가 — 미충족
          조건:{' '}
          {publishBlockers.conditions.map((condition) => BLOCKER_LABELS[condition]).join(', ')}
        </p>
      ) : null}

      {productsQuery.isPending ? (
        <div className="mt-5">
          <EmptyState>불러오는 중…</EmptyState>
        </div>
      ) : productsQuery.isError ? (
        <div className="mt-5">
          <EmptyState isError>목록을 불러오지 못했습니다: {productsQuery.error.message}</EmptyState>
        </div>
      ) : productsQuery.data.items.length === 0 ? (
        <div className="mt-5">
          <EmptyState>조건에 맞는 상품이 없습니다.</EmptyState>
        </div>
      ) : (
        <>
          <div className={`mt-5 ${tableStyles.card}`}>
            <table className={tableStyles.table}>
              <thead className={tableStyles.head}>
                <tr>
                  <th className={tableStyles.th}>상품</th>
                  <th className={tableStyles.th}>상태</th>
                  <th className={tableStyles.th}>대표 단가</th>
                  <th className={tableStyles.th}>최소 수량</th>
                  <th className={tableStyles.th}>수정일</th>
                  <th className={tableStyles.th}>관리</th>
                </tr>
              </thead>
              <tbody>
                {productsQuery.data.items.map((product) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    isPublishPending={publishAction.isPending}
                    isHidePending={hideAction.isPending}
                    isDeletePending={deleteAction.isPending}
                    onPublish={(id, name) => void handlePublish(id, name)}
                    onHide={handleHide}
                    onDeleteDraft={handleDeleteDraft}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            className="mt-5 justify-center"
            page={productsQuery.data.meta.page}
            totalPages={productsQuery.data.meta.totalPages}
            onPageChange={onPageChange}
          />
        </>
      )}
    </>
  );
};
